import { useEffect, useRef, useState } from "react";
import type { PlcMachineCommandAction, PlcMachineCommandPanel } from "../../../contracts/plc-machine-commands.js";
import { productionGateway } from "./production-gateway";
import { canSendMachineCommand } from "./machine-command-presentation";

export function useMachineCommandsWorkspace(terminalCode: string, sessionId: string) {
  const key = `${terminalCode}:${sessionId}`;
  const [reading, setReading] = useState<{ key: string; panel: PlcMachineCommandPanel; checkedAt: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PlcMachineCommandAction | null>(null);
  const [busy, setBusy] = useState(false);
  const [requestUncertain, setRequestUncertain] = useState(false);
  const [acknowledgedCommandId, setAcknowledgedCommandId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now);
  const inFlight = useRef(false);
  const scope = useRef(key); scope.current = key;
  useEffect(() => {
    let disposed = false;
    let controller: AbortController | null = null;
    let timer: ReturnType<typeof setTimeout>;
    setSelected(null); setError(null); setRequestUncertain(false);
    const read = async () => {
      controller = new AbortController();
      const timeout = setTimeout(() => controller?.abort(), 5000);
      try {
        const panel = await productionGateway.machineCommandPanel(terminalCode, sessionId, controller.signal);
        if (!disposed) { setReading({ key, panel, checkedAt: Date.now() }); setError(null); }
      } catch (failure) {
        if (!disposed) setError(failure instanceof Error ? failure.message : "Makine komut bağlantısı doğrulanamadı.");
      } finally { clearTimeout(timeout); if (!disposed) timer = setTimeout(() => void read(), 2000); }
    };
    void read();
    const clock = setInterval(() => setNow(Date.now()), 1000);
    return () => { disposed = true; controller?.abort(); clearTimeout(timer); clearInterval(clock); };
  }, [key, terminalCode, sessionId]);
  const current = reading?.key === key ? reading : null;
  const connected = !error && current !== null && now - current.checkedAt <= 5000;
  const uncertain = requestUncertain || (current?.panel.latestCommand?.status === "UNKNOWN" && current.panel.latestCommand.id !== acknowledgedCommandId);
  const allowed = (action: PlcMachineCommandAction) => !busy && !uncertain && canSendMachineCommand(current?.panel ?? null, action, connected, now);
  const send = async () => {
    if (!selected || !allowed(selected) || inFlight.current) return;
    const action = selected;
    inFlight.current = true; setBusy(true); setSelected(null);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    try {
      // A single human confirmation creates one request. A lost response is never replayed automatically.
      const command = await productionGateway.createMachineCommand(terminalCode, sessionId, action, crypto.randomUUID(), controller.signal);
      if (scope.current === key) setReading(previous => previous?.key === key ? { ...previous, panel: { ...previous.panel, latestCommand: command } } : previous);
    } catch (failure) {
      if (scope.current === key) { setRequestUncertain(true); setError(failure instanceof Error ? failure.message : "Komut sonucu doğrulanamadı."); }
    } finally {
      clearTimeout(timeout); inFlight.current = false;
      if (scope.current === key) setBusy(false);
    }
  };
  return { panel: current?.panel ?? null, connected, now, error, selected, select: setSelected, busy, uncertain,
    acknowledgeFieldCheck: () => { setRequestUncertain(false); setAcknowledgedCommandId(current?.panel.latestCommand?.id ?? null); }, allowed, send };
}
